// ============================================
// ADMIN DASHBOARD - CENTRE DE CONTRÔLE
// Version simplifiée et unifiée
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    TrendingUp, DollarSign, Package, AlertTriangle, 
    RefreshCcw, Users, ChefHat, Clock,
    CreditCard, CheckCircle, Warehouse,
    MapPin, Activity, Lock, Unlock,
    GlassWater, Tags, XCircle, ShoppingCart,
    CalendarCheck, MessageSquare, Target, Trophy, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import CompactTableCard from '../components/CompactTableCard';
import StockAlertBanner from '../components/StockAlertBanner';

// ============================================
// OBJECTIF CA PROGRESS CARD
// ============================================
const ObjectifCACard = ({ kpis, onSettingsClick }) => {
    const percentage = kpis?.objectif_pourcentage || 0;
    const isAchieved = kpis?.objectif_atteint || false;
    const objectif = kpis?.objectif_ca || 0;
    const ca = kpis?.ca_du_jour || 0;
    
    const getProgressColor = () => {
        if (percentage >= 100) return 'bg-emerald-500';
        if (percentage >= 80) return 'bg-emerald-400';
        if (percentage >= 50) return 'bg-orange-400';
        return 'bg-red-400';
    };
    
    const getTextColor = () => {
        if (percentage >= 100) return 'text-emerald-600';
        if (percentage >= 80) return 'text-emerald-500';
        if (percentage >= 50) return 'text-orange-500';
        return 'text-red-500';
    };

    if (objectif === 0) {
        return (
            <Card className="border-0 shadow-md bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                <Target className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-500">Objectif CA du jour</p>
                                <p className="text-xs text-gray-400">Aucun objectif défini</p>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={onSettingsClick}>
                            <Settings className="w-4 h-4 mr-1" />
                            Définir
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={`border-0 shadow-md ${isAchieved ? 'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 ring-2 ring-emerald-500' : 'bg-white dark:bg-gray-900'}`}>
            <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isAchieved ? 'bg-emerald-500' : 'bg-gradient-to-br from-amber-400 to-orange-500'}`}>
                            {isAchieved ? <Trophy className="w-5 h-5 text-white" /> : <Target className="w-5 h-5 text-white" />}
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Objectif CA du jour</p>
                            {kpis?.objectif_message && (
                                <p className="text-xs text-gray-500 italic">{kpis.objectif_message}</p>
                            )}
                        </div>
                    </div>
                    {isAchieved && (
                        <Badge className="bg-emerald-500 text-white animate-pulse">
                            Objectif atteint! 🎉
                        </Badge>
                    )}
                </div>
                
                <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                        <span className={`font-bold ${getTextColor()}`}>
                            {kpis?.ca_du_jour_formatted || '0 CFA'}
                        </span>
                        <span className="text-gray-500">
                            / {kpis?.objectif_ca_formatted || '0 CFA'}
                        </span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                            className={`h-full ${getProgressColor()} transition-all duration-500 rounded-full`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <span className={`text-lg font-bold ${getTextColor()}`}>
                            {percentage}%
                        </span>
                        <span className="text-xs text-gray-500">
                            {percentage >= 100 
                                ? `+${Math.round(ca - objectif).toLocaleString('fr-FR')} CFA de bonus!` 
                                : `Reste ${Math.round(objectif - ca).toLocaleString('fr-FR')} CFA`
                            }
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// ============================================
// KPI CARD COMPONENT
// ============================================
const KPICard = ({ title, value, subtitle, icon: Icon, status = 'default', onClick, badge = null }) => {
    const statusColors = {
        green: 'border-l-4 border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20',
        orange: 'border-l-4 border-l-orange-500 bg-orange-50/50 dark:bg-orange-950/20',
        red: 'border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-950/20',
        blue: 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20',
        default: ''
    };
    
    return (
        <Card 
            className={`border-0 shadow-md hover:shadow-lg transition-all ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''} ${statusColors[status]}`}
            onClick={onClick}
        >
            <CardContent className="p-4">
                <div className="flex items-start justify-between">
                    <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
                        <div className="flex items-center gap-2">
                            <p className={`text-xl font-bold ${
                                status === 'red' ? 'text-red-600' : 
                                status === 'orange' ? 'text-orange-600' : 
                                status === 'green' ? 'text-emerald-600' : 
                                'text-gray-900 dark:text-white'
                            }`}>
                                {value}
                            </p>
                            {badge && (
                                <Badge variant={badge.variant || 'secondary'} className="text-xs">
                                    {badge.text}
                                </Badge>
                            )}
                        </div>
                        {subtitle && (
                            <p className="text-[10px] text-gray-500">{subtitle}</p>
                        )}
                    </div>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        status === 'red' ? 'bg-red-100 text-red-600' :
                        status === 'orange' ? 'bg-orange-100 text-orange-600' :
                        status === 'green' ? 'bg-emerald-100 text-emerald-600' :
                        'bg-gray-100 text-gray-600 dark:bg-gray-800'
                    }`}>
                        <Icon className="w-5 h-5" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// ============================================
// EMPLOYÉ DU MOIS CARD (Compact)
// ============================================
const EmployeeOfMonthCard = ({ employee }) => {
    if (!employee || !employee.name) return null;
    
    return (
        <Card className="border-0 shadow-md bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 h-full">
            <CardContent className="p-3 h-full flex flex-col justify-center">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        {employee.photo_url ? (
                            <img 
                                src={employee.photo_url} 
                                alt={employee.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-amber-400"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center border-2 border-amber-400">
                                <span className="text-lg font-bold text-amber-700 dark:text-amber-300">
                                    {employee.name.charAt(0).toUpperCase()}
                                </span>
                            </div>
                        )}
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                            <Trophy className="w-3 h-3 text-white" />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0 mb-1">
                            Employé du mois
                        </Badge>
                        <p className="font-semibold text-gray-900 dark:text-white truncate">{employee.name}</p>
                        {employee.position && (
                            <p className="text-xs text-gray-500 truncate">{employee.position}</p>
                        )}
                    </div>
                </div>
                {employee.message && (
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 italic line-clamp-1">
                        "{employee.message}"
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

// ============================================
// STATION CARD - Kitchen/Bar Mini Display
// ============================================
const StationCard = ({ title, icon: Icon, stats, color, onClick }) => (
    <Card 
        className={`border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02]`}
        onClick={onClick}
    >
        <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                    <p className="text-lg font-bold text-gray-600">{stats.waiting || 0}</p>
                    <p className="text-[10px] text-gray-500">Attente</p>
                </div>
                <div>
                    <p className="text-lg font-bold text-blue-600">{stats.preparing || 0}</p>
                    <p className="text-[10px] text-gray-500">En cours</p>
                </div>
                <div>
                    <p className="text-lg font-bold text-emerald-600">{stats.ready || 0}</p>
                    <p className="text-[10px] text-gray-500">Prêts</p>
                </div>
                <div>
                    <p className="text-lg font-bold text-red-600">{stats.problem || 0}</p>
                    <p className="text-[10px] text-gray-500">Problème</p>
                </div>
            </div>
            {stats.avgTime && (
                <div className="mt-2 pt-2 border-t text-center">
                    <p className="text-xs text-gray-500">Temps moyen: <span className="font-semibold">{stats.avgTime} min</span></p>
                </div>
            )}
        </CardContent>
    </Card>
);

// ============================================
// EMPTY STATE
// ============================================
const EmptyState = ({ message, icon: Icon = Package }) => (
    <div className="flex flex-col items-center justify-center py-6 text-gray-400">
        <Icon className="w-8 h-8 mb-2 opacity-50" />
        <p className="text-sm">{message}</p>
    </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const AdminDashboardPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [kpis, setKpis] = useState(null);
    const [registers, setRegisters] = useState([]);
    const [tables, setTables] = useState([]);
    const [kitchenOrders, setKitchenOrders] = useState([]);
    const [barOrders, setBarOrders] = useState([]);
    const [liveFeed, setLiveFeed] = useState([]);
    const [lowStockProducts, setLowStockProducts] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [objectifNotified, setObjectifNotified] = useState(false);
    
    const intervalRef = useRef(null);
    
    // Fonction pour forcer la libération de toutes les tables
    const handleForceReleaseAllTables = async () => {
        if (!window.confirm('⚠️ Forcer la libération de TOUTES les tables occupées ?\n\nCette action va:\n- Vider toutes les commandes en cours\n- Annuler tous les paiements en cours\n- Remettre toutes les tables en "libre"\n\nCette action est IRRÉVERSIBLE.')) {
            return;
        }
        
        try {
            const response = await api().post('/tables/force-release-all');
            if (response.data?.success) {
                toast.success(`✅ ${response.data.message}`);
                fetchData(true);
            } else {
                toast.error('Erreur lors de la libération');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la libération des tables');
        }
    };
    
    // Audio pour notification
    const playSuccessSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1);
            oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2);
            
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.5);
        } catch (e) {
            console.log('Audio not supported');
        }
    };
    
    // ============================================
    // FETCH DATA
    // ============================================
    const fetchData = useCallback(async (showToast = false) => {
        try {
            const [kpisRes, registersRes, tablesRes, kitchenRes, barRes, feedRes, lowStockRes] = await Promise.all([
                api().get('/dashboard-kpi/main-stats').catch(() => null),
                api().get('/cash-registers').catch(() => null),
                api().get('/tables').catch(() => null),
                api().get('/kitchen/tickets').catch(() => null),
                api().get('/bar/tickets').catch(() => null),
                api().get('/pos/admin/dashboard/live-feed?limit=10').catch(() => null),
                api().get('/dashboard-kpi/low-stock-products?limit=5').catch(() => null)
            ]);
            
            if (kpisRes?.data) {
                const newKpis = kpisRes.data;
                
                // Notification si objectif atteint pour la première fois
                if (newKpis.objectif_atteint && !objectifNotified && newKpis.objectif_ca > 0) {
                    setObjectifNotified(true);
                    playSuccessSound();
                    toast.success(
                        <div className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            <div>
                                <p className="font-bold">Objectif atteint! 🎉</p>
                                <p className="text-sm">{newKpis.ca_du_jour_formatted} / {newKpis.objectif_ca_formatted}</p>
                            </div>
                        </div>,
                        { duration: 10000 }
                    );
                }
                
                setKpis(newKpis);
            }
            setRegisters(registersRes?.data || []);
            setTables(tablesRes?.data || []);
            setKitchenOrders(kitchenRes?.data || []);
            setBarOrders(barRes?.data || []);
            setLiveFeed(feedRes?.data?.activities || []);
            setLowStockProducts(lowStockRes?.data || []);
            setLastUpdate(new Date());
            
            if (showToast) toast.success('Données actualisées');
        } catch (error) {
            console.error('Error fetching data:', error);
            if (showToast) toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    useEffect(() => {
        if (autoRefresh) {
            intervalRef.current = setInterval(() => fetchData(false), 15000);
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [autoRefresh, fetchData]);
    
    // ============================================
    // COMPUTED VALUES
    // ============================================
    const openRegisters = registers.filter(r => r.status === 'open');
    const lockedRegisters = registers.filter(r => r.is_locked);
    const totalRegisterSales = registers.reduce((sum, r) => sum + (r.total_sales || 0), 0);
    
    const tablesOccupied = tables.filter(t => ['occupied', 'occupee', 'en_cours'].includes(t.status)).length;
    const tablesAvailable = tables.filter(t => ['available', 'libre', 'free'].includes(t.status)).length;
    const tablesWaiting = tables.filter(t => t.status === 'waiting_payment').length;
    
    const kitchenStats = {
        waiting: kitchenOrders.filter(o => ['nouveau', 'pending', 'en_attente'].includes(o.kitchen_status || o.status)).length,
        preparing: kitchenOrders.filter(o => ['en_preparation', 'preparing', 'en_cours'].includes(o.kitchen_status || o.status)).length,
        ready: kitchenOrders.filter(o => ['pret', 'ready'].includes(o.kitchen_status)).length,
        problem: kitchenOrders.filter(o => o.kitchen_status === 'probleme').length,
        avgTime: kitchenOrders.length > 0 ? Math.round(kitchenOrders.reduce((sum, o) => sum + (o.wait_time_minutes || 0), 0) / kitchenOrders.length) : 0
    };
    
    const barStats = {
        waiting: barOrders.filter(o => ['nouveau', 'pending', 'en_attente'].includes(o.bar_status || '')).length,
        preparing: barOrders.filter(o => ['en_preparation', 'en_cours'].includes(o.bar_status || '')).length,
        ready: barOrders.filter(o => o.bar_status === 'pret').length,
        problem: barOrders.filter(o => o.bar_status === 'probleme').length,
        avgTime: barOrders.length > 0 ? Math.round(barOrders.reduce((sum, o) => sum + (o.wait_time_minutes || 0), 0) / barOrders.length) : 0
    };
    
    // ============================================
    // RENDER
    // ============================================
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    return (
        <div className="space-y-6 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Centre de Contrôle
                    </h1>
                    <p className="text-sm text-gray-500">
                        {lastUpdate ? `Mis à jour à ${lastUpdate.toLocaleTimeString('fr-FR')}` : 'Chargement...'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button 
                        variant={autoRefresh ? "default" : "outline"}
                        size="sm" 
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className="gap-2"
                    >
                        <Activity className={`w-4 h-4 ${autoRefresh ? 'animate-pulse' : ''}`} />
                        {autoRefresh ? 'Live' : 'Pause'}
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => fetchData(true)}
                        className="gap-2"
                    >
                        <RefreshCcw className="w-4 h-4" />
                        Actualiser
                    </Button>
                    <Button 
                        variant="destructive" 
                        size="sm" 
                        onClick={handleForceReleaseAllTables}
                        className="gap-2"
                        title="Forcer la libération de toutes les tables (ADMIN)"
                    >
                        <Unlock className="w-4 h-4" />
                        Libérer tables
                    </Button>
                </div>
            </div>

            {/* Stock Alert Banner */}
            <StockAlertBanner />

            {/* ============================================ */}
            {/* OBJECTIF CA + EMPLOYÉ DU MOIS */}
            {/* ============================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                    <ObjectifCACard 
                        kpis={kpis} 
                        onSettingsClick={() => navigate('/motivation-management')}
                    />
                </div>
                <div>
                    <EmployeeOfMonthCard employee={kpis?.employee_of_month} />
                </div>
            </div>

            {/* ============================================ */}
            {/* LIGNE 1 - KPIs BUSINESS */}
            {/* ============================================ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title="Chiffre d'affaires"
                    value={kpis?.ca_du_jour_formatted || '0 CFA'}
                    subtitle={kpis?.ventes_count > 0 ? `${kpis.ventes_count} vente(s)` : "Aucune vente aujourd'hui"}
                    icon={DollarSign}
                    status={kpis?.ca_du_jour > 0 ? 'green' : 'default'}
                />
                <KPICard
                    title="Achats du jour"
                    value={kpis?.achats_du_jour_formatted || '0 CFA'}
                    subtitle={kpis?.achats_count > 0 ? `${kpis.achats_count} achat(s)` : "Aucun achat"}
                    icon={ShoppingCart}
                />
                <KPICard
                    title="Valeur du stock"
                    value={kpis?.valeur_stock_formatted || '0 CFA'}
                    subtitle={`${kpis?.total_products || 0} produits`}
                    icon={Warehouse}
                />
                <KPICard
                    title="Stock bas"
                    value={kpis?.stock_bas_count || 0}
                    subtitle={kpis?.stock_bas_count > 0 ? "À réapprovisionner" : "Stock OK"}
                    icon={AlertTriangle}
                    status={kpis?.stock_bas_count > 0 ? 'orange' : 'green'}
                    onClick={() => navigate('/stock-alerts')}
                />
            </div>

            {/* ============================================ */}
            {/* LIGNE 2 - SUIVI PRODUITS */}
            {/* ============================================ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title="Total produits"
                    value={kpis?.total_products || 0}
                    subtitle="Produits actifs"
                    icon={Package}
                    onClick={() => navigate('/products')}
                />
                <KPICard
                    title="Produits périmés"
                    value={kpis?.produits_perimes || 0}
                    subtitle={kpis?.produits_perimes > 0 ? "À retirer" : "Aucun"}
                    icon={XCircle}
                    status={kpis?.produits_perimes > 0 ? 'red' : 'green'}
                />
                <KPICard
                    title="Péremption proche"
                    value={kpis?.peremption_proche || 0}
                    subtitle="Dans les 7 jours"
                    icon={Clock}
                    status={kpis?.peremption_proche > 0 ? 'orange' : 'green'}
                />
                <KPICard
                    title="Catégories"
                    value={kpis?.total_categories || 0}
                    subtitle="Catégories produits"
                    icon={Tags}
                />
            </div>

            {/* ============================================ */}
            {/* LIGNE 3 - OPÉRATIONNEL */}
            {/* ============================================ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    title="Caisses ouvertes"
                    value={`${openRegisters.length}/${registers.length}`}
                    subtitle={`${lockedRegisters.length} verrouillée(s)`}
                    icon={CreditCard}
                    status={openRegisters.length > 0 ? 'green' : 'default'}
                    badge={openRegisters.length > 0 ? { text: 'Active', variant: 'default' } : null}
                    onClick={() => navigate('/register-management')}
                />
                <KPICard
                    title="Tables"
                    value={`${tablesOccupied}/${tables.length}`}
                    subtitle={`${tablesAvailable} libres${tablesWaiting > 0 ? ` • ${tablesWaiting} paiement` : ''}`}
                    icon={MapPin}
                    status={tablesOccupied > 0 ? 'blue' : 'default'}
                    onClick={() => navigate('/tables')}
                />
                <KPICard
                    title="Cuisine"
                    value={kitchenStats.waiting + kitchenStats.preparing}
                    subtitle={kitchenStats.waiting + kitchenStats.preparing > 0 ? `${kitchenStats.ready} prêts` : "Aucun ticket"}
                    icon={ChefHat}
                    status={kitchenStats.problem > 0 ? 'red' : kitchenStats.waiting > 5 ? 'orange' : 'default'}
                    badge={kitchenStats.problem > 0 ? { text: `${kitchenStats.problem} problème`, variant: 'destructive' } : null}
                    onClick={() => navigate('/kitchen-dashboard')}
                />
                <KPICard
                    title="Bar"
                    value={barStats.waiting + barStats.preparing}
                    subtitle={barStats.waiting + barStats.preparing > 0 ? `${barStats.ready} prêts` : "Aucun ticket"}
                    icon={GlassWater}
                    status={barStats.problem > 0 ? 'red' : barStats.waiting > 5 ? 'orange' : 'default'}
                    badge={barStats.problem > 0 ? { text: `${barStats.problem} problème`, variant: 'destructive' } : null}
                    onClick={() => navigate('/bar-dashboard')}
                />
            </div>

            {/* ============================================ */}
            {/* STATIONS CUISINE / BAR */}
            {/* ============================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <StationCard
                    title="Cuisine"
                    icon={ChefHat}
                    stats={kitchenStats}
                    color="bg-gradient-to-br from-orange-500 to-orange-600"
                    onClick={() => navigate('/kitchen-dashboard')}
                />
                <StationCard
                    title="Bar"
                    icon={GlassWater}
                    stats={barStats}
                    color="bg-gradient-to-br from-purple-500 to-purple-600"
                    onClick={() => navigate('/bar-dashboard')}
                />
            </div>

            {/* ============================================ */}
            {/* CAISSES */}
            {/* ============================================ */}
            <Card className="border-0 shadow-md">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-emerald-500" />
                            Caisses ({openRegisters.length}/{registers.length})
                        </CardTitle>
                        <Button variant="outline" size="sm" onClick={() => navigate('/register-management')}>
                            Gérer
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {registers.length === 0 ? (
                        <EmptyState message="Aucune caisse configurée" icon={CreditCard} />
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            {registers.map((register, index) => (
                                <div 
                                    key={register.id || index}
                                    className={`p-3 rounded-lg border-2 ${
                                        register.status === 'open' 
                                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20' 
                                            : 'border-gray-200 bg-gray-50 dark:bg-gray-800'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="font-medium text-sm truncate">{register.name}</p>
                                        {register.is_locked ? (
                                            <Lock className="w-3 h-3 text-red-500" />
                                        ) : register.status === 'open' ? (
                                            <Unlock className="w-3 h-3 text-emerald-500" />
                                        ) : null}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        {register.status === 'open' ? 'Ouverte' : 'Fermée'}
                                    </p>
                                    <p className="text-sm font-bold mt-1">
                                        {(register.total_sales || 0).toLocaleString('fr-FR')} F
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ============================================ */}
            {/* PLAN DE SALLE */}
            {/* ============================================ */}
            <Card className="border-0 shadow-md">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-blue-500" />
                            Salle ({tablesOccupied}/{tables.length} occupées)
                        </CardTitle>
                        <div className="flex items-center gap-4 text-xs">
                            <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-emerald-500"></span> Libre ({tablesAvailable})
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-orange-500"></span> Occupée ({tablesOccupied})
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="w-3 h-3 rounded-full bg-red-500"></span> Paiement ({tablesWaiting})
                            </span>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {tables.length === 0 ? (
                        <EmptyState message="Aucune table configurée" icon={MapPin} />
                    ) : (
                        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                            {tables.slice(0, 24).map((table, index) => (
                                <CompactTableCard
                                    key={table.id || index}
                                    table={table}
                                    onClick={() => navigate(`/pos?table=${table.id}`)}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ============================================ */}
            {/* ACTIVITÉ EN DIRECT + STOCK BAS */}
            {/* ============================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activité en direct */}
                <Card className="border-0 shadow-md">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <Activity className="w-4 h-4 text-emerald-500" />
                            Activité en direct
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {liveFeed.length === 0 ? (
                            <EmptyState message="Aucune activité récente" icon={Activity} />
                        ) : (
                            <ScrollArea className="h-[200px]">
                                <div className="space-y-2">
                                    {liveFeed.map((activity, index) => (
                                        <div 
                                            key={index}
                                            className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                                        >
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                activity.type === 'payment' ? 'bg-emerald-100 text-emerald-600' :
                                                activity.type === 'order' ? 'bg-blue-100 text-blue-600' :
                                                'bg-gray-100 text-gray-600'
                                            }`}>
                                                {activity.type === 'payment' ? <DollarSign className="w-4 h-4" /> :
                                                 activity.type === 'order' ? <CheckCircle className="w-4 h-4" /> :
                                                 <Activity className="w-4 h-4" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{activity.description || activity.message}</p>
                                                <p className="text-xs text-gray-500">
                                                    {activity.table_name || activity.table} • {activity.user_name || activity.server}
                                                </p>
                                            </div>
                                            {activity.amount && (
                                                <p className="text-sm font-bold text-emerald-600">
                                                    {activity.amount.toLocaleString('fr-FR')} F
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}
                    </CardContent>
                </Card>

                {/* Stock bas */}
                <Card className="border-0 shadow-md">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-orange-500" />
                                Produits en stock bas
                            </CardTitle>
                            {lowStockProducts.length > 0 && (
                                <Badge variant="destructive">{lowStockProducts.length}</Badge>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        {lowStockProducts.length === 0 ? (
                            <EmptyState message="Aucun produit en stock bas" icon={CheckCircle} />
                        ) : (
                            <ScrollArea className="h-[200px]">
                                <div className="space-y-2">
                                    {lowStockProducts.map((product, index) => (
                                        <div 
                                            key={product.id || index}
                                            className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
                                        >
                                            <div>
                                                <p className="font-medium text-sm">{product.name}</p>
                                                <p className="text-xs text-gray-500">{product.category || 'Non catégorisé'}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-orange-600">
                                                    {product.stock_quantity} {product.unit_base || ''}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    Min: {product.min_stock_alert}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}
                        {lowStockProducts.length > 0 && (
                            <Button 
                                variant="outline" 
                                className="w-full mt-3"
                                onClick={() => navigate('/stock-alerts')}
                            >
                                Voir tous les produits
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* ============================================ */}
            {/* ACCÈS RAPIDE */}
            {/* ============================================ */}
            <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Accès rapide</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    <Link to="/pos" className="block">
                        <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-emerald-500 to-emerald-600">
                            <CardContent className="p-4 flex items-center gap-3">
                                <CreditCard className="w-5 h-5 text-white" />
                                <span className="font-semibold text-white">Caisse</span>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/tables" className="block">
                        <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-blue-500 to-blue-600">
                            <CardContent className="p-4 flex items-center gap-3">
                                <MapPin className="w-5 h-5 text-white" />
                                <span className="font-semibold text-white">Tables</span>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/kitchen-dashboard" className="block">
                        <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-orange-500 to-orange-600">
                            <CardContent className="p-4 flex items-center gap-3">
                                <ChefHat className="w-5 h-5 text-white" />
                                <span className="font-semibold text-white">Cuisine</span>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/bar-dashboard" className="block">
                        <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-purple-500 to-purple-600">
                            <CardContent className="p-4 flex items-center gap-3">
                                <GlassWater className="w-5 h-5 text-white" />
                                <span className="font-semibold text-white">Bar</span>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/team-chat" className="block">
                        <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-indigo-500 to-indigo-600">
                            <CardContent className="p-4 flex items-center gap-3">
                                <MessageSquare className="w-5 h-5 text-white" />
                                <span className="font-semibold text-white">Messagerie</span>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/inventory" className="block">
                        <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-teal-500 to-teal-600">
                            <CardContent className="p-4 flex items-center gap-3">
                                <Warehouse className="w-5 h-5 text-white" />
                                <span className="font-semibold text-white">Stock</span>
                            </CardContent>
                        </Card>
                    </Link>
                    <Link to="/reservations" className="block">
                        <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:scale-[1.02] bg-gradient-to-br from-pink-500 to-pink-600">
                            <CardContent className="p-4 flex items-center gap-3">
                                <CalendarCheck className="w-5 h-5 text-white" />
                                <span className="font-semibold text-white">Réservations</span>
                            </CardContent>
                        </Card>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardPage;
